<template>
  <div>
    <head>
      <meta charset="utf-8"/>
      <meta content="width=device-width, initial-scale=1.0" name="viewport"/>
      <link href="assets/css/bootstrap.min.css" rel="stylesheet" type="text/css"/>
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css"/>
      <link href="assets/css/app.min.css" rel="stylesheet" type="text/css"/>
      <link href="assets/css/custom.css" rel="stylesheet" type="text/css"/>
    </head>
    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar/>
      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- HEADER -->
            <div class="crh-header">
              <div class="crh-header-left">
                <div class="crh-header-icon"><i class="bx bx-revision"></i></div>
                <div>
                  <h1 class="crh-title">Correction History</h1>
                  <div class="crh-breadcrumb">
                    <i class="bx bx-home-alt"></i>
                    <span @click="gotTo('dashboard')" class="crh-bc-link">Dashboard</span>
                    <span class="crh-bc-sep">/</span>
                    <span>Correction History</span>
                  </div>
                </div>
              </div>
              <div class="crh-header-stat">
                <span class="crh-stat-val">{{ pagination.total }}</span>
                <span class="crh-stat-lbl">Total Corrections</span>
              </div>
            </div>

            <!-- TABS -->
            <div class="crh-tabs">
              <button @click="activeTab = 'analytics'; loadAnalytics()" :class="['crh-tab', activeTab === 'analytics' ? 'active' : '']">
                <i class="bx bx-bar-chart-alt-2 me-1"></i> Analytics & Trends
              </button>
              <button @click="activeTab = 'list'" :class="['crh-tab', activeTab === 'list' ? 'active' : '']">
                <i class="bx bx-list-ul me-1"></i> Correction List
              </button>
            </div>

            <!-- ═══════════════════════════════════════════════
                 TAB 1 — CORRECTION LIST
            ════════════════════════════════════════════════ -->
            <div v-if="activeTab === 'list'">

              <!-- Filters -->
              <div class="crh-filters">
                <input v-model="filters.keyword" type="text" class="crh-search"
                       placeholder="Search bill no, name, transaction code, corrected by..."
                       @keyup.enter="loadList"/>
                <div class="crh-date-group">
                  <label class="crh-date-lbl">Approved From</label>
                  <input v-model="filters.dateFrom" type="date" class="crh-datepicker"/>
                </div>
                <div class="crh-date-group">
                  <label class="crh-date-lbl">Approved To</label>
                  <input v-model="filters.dateTo" type="date" class="crh-datepicker"/>
                </div>
                <button @click="loadList" class="crh-search-btn" :disabled="loadingList">
                  <i class="bx bx-search me-1"></i> Search
                </button>
                <button @click="resetFilters" class="crh-reset-btn"><i class="bx bx-reset"></i></button>
                <button v-if="corrections.length > 0" @click="downloadListPDF" class="crh-pdf-btn" :disabled="generatingListPDF">
                  <i class="bx" :class="generatingListPDF ? 'bx-loader bx-spin' : 'bx-download'"></i>
                  {{ generatingListPDF ? 'Generating...' : 'Download PDF' }}
                </button>
              </div>

              <div v-if="loadingList" class="crh-loading">
                <div class="spinner-border text-primary"></div>
                <p>Loading corrections...</p>
              </div>

              <div v-else-if="corrections.length === 0" class="crh-empty">
                <i class="bx bx-check-circle crh-empty-icon"></i>
                <h5>No Corrections Found</h5>
                <p class="text-muted">No approved corrections match your search criteria.</p>
              </div>

              <div v-else class="crh-table-wrap">
                <table class="table crh-table">
                  <thead>
                  <tr>
                    <th>#</th>
                    <th>Transaction</th>
                    <th>Bill No</th>
                    <th>Amount</th>
                    <th>Correction Details</th>
                    <th>Requested By</th>
                    <th>Request Date</th>
                    <th>Approved By</th>
                    <th>Approval Date</th>
                    <th></th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="c in corrections" :key="c.id">
                    <td class="text-muted">{{ c.id }}</td>
                    <td>
                      <div class="crh-tx-code">{{ c.transaction_code }}</div>
                      <div class="crh-tx-sub">{{ c.names }}</div>
                      <span :class="['crh-type-badge', (c.payment_type || '').toLowerCase()]">{{ c.payment_type }}</span>
                    </td>
                    <td>
                      <div class="fw-bold">{{ c.billNo }}</div>
                      <small class="text-muted">{{ formatDate(c.transactionDate) }}</small>
                    </td>
                    <td class="fw-bold">{{ formatPrice(c.receiptAmount || c.amount) }}</td>
                    <td><div class="crh-correction-box">{{ c.correctionDetails || '—' }}</div></td>
                    <td>
                      <div class="fw-bold">{{ c.correctionRequestedBy || '—' }}</div>
                      <small class="text-muted">{{ c.correctionRequestedByIDNo }}</small>
                    </td>
                    <td>{{ formatDateTime(c.correctionDate) }}</td>
                    <td>
                      <div class="fw-bold text-success">{{ c.correctionApprovalBy || '—' }}</div>
                      <small class="text-muted">{{ c.correctionApprovalByIDNo }}</small>
                    </td>
                    <td><span class="crh-approved-badge"><i class="bx bx-check-double me-1"></i>{{ formatDateTime(c.correctionDateApproved) }}</span></td>
                    <td><button @click="openDetail(c)" class="crh-detail-btn"><i class="bx bx-detail"></i> View</button></td>
                  </tr>
                  </tbody>
                </table>

                <!-- Pagination -->
                <div class="crh-pagination" v-if="pagination.totalPages > 1">
                  <button @click="changePage(pagination.page - 1)" :disabled="pagination.page === 1" class="crh-page-btn">
                    <i class="bx bx-chevron-left"></i>
                  </button>
                  <span class="crh-page-info">Page {{ pagination.page }} of {{ pagination.totalPages }} ({{ pagination.total }} records)</span>
                  <button @click="changePage(pagination.page + 1)" :disabled="pagination.page === pagination.totalPages" class="crh-page-btn">
                    <i class="bx bx-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- ═══════════════════════════════════════════════
                 TAB 2 — ANALYTICS & TRENDS
            ════════════════════════════════════════════════ -->
            <div v-if="activeTab === 'analytics'">

              <!-- Analytics date filter -->
              <div class="crh-filters">
                <div class="crh-date-group">
                  <label class="crh-date-lbl">From</label>
                  <input v-model="aDateFrom" type="date" class="crh-datepicker"/>
                </div>
                <div class="crh-date-group">
                  <label class="crh-date-lbl">To</label>
                  <input v-model="aDateTo" type="date" class="crh-datepicker"/>
                </div>
                <button @click="loadAnalytics" class="crh-search-btn" :disabled="loadingAnalytics">
                  <i class="bx bx-filter me-1"></i> Apply
                </button>
                <div class="crh-quick-btns">
                  <button @click="setQuick('thisYear')"  :class="['crh-quick', quickActive==='thisYear'  ? 'active':'']">This Year</button>
                  <button @click="setQuick('thisMonth')" :class="['crh-quick', quickActive==='thisMonth' ? 'active':'']">This Month</button>
                  <button @click="setQuick('last3')"     :class="['crh-quick', quickActive==='last3'     ? 'active':'']">Last 3 Months</button>
                </div>
                <button v-if="analyticsLoaded" @click="downloadPDF" class="crh-pdf-btn" :disabled="generatingPDF">
                  <i class="bx" :class="generatingPDF ? 'bx-loader bx-spin' : 'bx-download'"></i>
                  {{ generatingPDF ? 'Generating...' : 'Download PDF' }}
                </button>
              </div>

              <div v-if="loadingAnalytics" class="crh-loading">
                <div class="spinner-border text-primary"></div>
                <p>Loading analytics...</p>
              </div>

              <template v-if="!loadingAnalytics && analyticsLoaded">

                <!-- Summary stat cards -->
                <div class="crh-stat-row">
                  <div class="crh-stat-card red">
                    <div class="crh-stat-icon"><i class="bx bx-revision"></i></div>
                    <div>
                      <div class="crh-stat-val2">{{ analytics.summary.totalCorrections }}</div>
                      <div class="crh-stat-lbl2">Total Corrections</div>
                    </div>
                  </div>
                  <div class="crh-stat-card orange">
                    <div class="crh-stat-icon"><i class="bx bx-money"></i></div>
                    <div>
                      <div class="crh-stat-val2">{{ formatPrice(analytics.summary.totalAmountCorrected) }}</div>
                      <div class="crh-stat-lbl2">Total Amount Corrected</div>
                    </div>
                  </div>
                  <div class="crh-stat-card blue">
                    <div class="crh-stat-icon"><i class="bx bx-user"></i></div>
                    <div>
                      <div class="crh-stat-val2">{{ analytics.summary.uniqueRequesters }}</div>
                      <div class="crh-stat-lbl2">Unique Requesters</div>
                    </div>
                  </div>
                  <div class="crh-stat-card green">
                    <div class="crh-stat-icon"><i class="bx bx-check-shield"></i></div>
                    <div>
                      <div class="crh-stat-val2">{{ analytics.summary.uniqueApprovers }}</div>
                      <div class="crh-stat-lbl2">Unique Approvers</div>
                    </div>
                  </div>
                </div>

                <!-- Monthly trend chart -->
                <div class="crh-card">
                  <div class="crh-card-hdr">
                    <h6><i class="bx bx-trending-up me-2"></i>Monthly Correction Trend</h6>
                    <small class="text-muted">Number of approved corrections per month</small>
                  </div>
                  <div class="crh-chart-wrap">
                    <div v-if="analytics.monthlyTrend.length === 0" class="crh-no-data">No data for selected period</div>
                    <div v-else class="crh-bar-chart">
                      <div class="crh-bar-group" v-for="m in analytics.monthlyTrend" :key="m.month">
                        <div class="crh-bar-col">
                          <div class="crh-bar-tooltip">{{ m.totalCorrections }} corrections<br/>{{ formatPrice(m.totalAmount) }}</div>
                          <div class="crh-bar" :style="{ height: barHeight(m.totalCorrections, maxMonthly) + 'px' }" :class="barColor(m.totalCorrections, maxMonthly)"></div>
                          <div class="crh-bar-val">{{ m.totalCorrections }}</div>
                        </div>
                        <div class="crh-bar-lbl">{{ m.monthLabel }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Top requesters + Top approvers -->
                <div class="crh-two-col">

                  <div class="crh-card">
                    <div class="crh-card-hdr danger">
                      <h6><i class="bx bx-user-x me-2"></i>Top Correction Requesters</h6>
                      <small>Officers who flag transactions most frequently</small>
                    </div>
                    <div v-if="analytics.topRequesters.length === 0" class="crh-no-data p-3">No data</div>
                    <div v-else class="crh-leaderboard">
                      <div v-for="(r, i) in analytics.topRequesters" :key="r.idNo" class="crh-lb-row">
                        <div class="crh-lb-rank" :class="i < 3 ? 'top' : ''">{{ i+1 }}</div>
                        <div class="crh-lb-avatar" :class="i===0?'gold':i===1?'silver':i===2?'bronze':''">{{ r.name ? r.name.charAt(0) : '?' }}</div>
                        <div class="crh-lb-info">
                          <div class="crh-lb-name">{{ r.name }}</div>
                          <div class="crh-lb-sub">{{ r.idNo }}</div>
                        </div>
                        <div class="crh-lb-right">
                          <div class="crh-lb-count danger">{{ r.totalRequests }}</div>
                          <div class="crh-lb-amount">{{ formatPrice(r.totalAmountRequested) }}</div>
                          <div class="crh-lb-bar"><div class="crh-lb-fill danger" :style="{ width: (r.totalRequests / analytics.topRequesters[0].totalRequests * 100) + '%' }"></div></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="crh-card">
                    <div class="crh-card-hdr warn">
                      <h6><i class="bx bx-user-check me-2"></i>Top Approvers</h6>
                      <small>Supervisors who approve corrections most frequently</small>
                    </div>
                    <div v-if="analytics.topApprovers.length === 0" class="crh-no-data p-3">No data</div>
                    <div v-else class="crh-leaderboard">
                      <div v-for="(a, i) in analytics.topApprovers" :key="a.idNo" class="crh-lb-row">
                        <div class="crh-lb-rank" :class="i < 3 ? 'top' : ''">{{ i+1 }}</div>
                        <div class="crh-lb-avatar" :class="i===0?'gold':i===1?'silver':i===2?'bronze':''">{{ a.name ? a.name.charAt(0) : '?' }}</div>
                        <div class="crh-lb-info">
                          <div class="crh-lb-name">{{ a.name }}</div>
                          <div class="crh-lb-sub">{{ a.idNo }}</div>
                        </div>
                        <div class="crh-lb-right">
                          <div class="crh-lb-count warn">{{ a.totalApprovals }}</div>
                          <div class="crh-lb-amount">{{ formatPrice(a.totalAmountApproved) }}</div>
                          <div class="crh-lb-bar"><div class="crh-lb-fill warn" :style="{ width: (a.totalApprovals / analytics.topApprovers[0].totalApprovals * 100) + '%' }"></div></div>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>

                <!-- Requester trend pivot table -->
                <div class="crh-card" v-if="requesterPivot.names.length > 0">
                  <div class="crh-card-hdr danger">
                    <h6><i class="bx bx-table me-2"></i>Requester Trend — Top 5 Over Time</h6>
                    <small>How many corrections each top requester made per month</small>
                  </div>
                  <div class="table-responsive">
                    <table class="table crh-table">
                      <thead>
                      <tr>
                        <th>Officer</th>
                        <th v-for="m in requesterPivot.months" :key="m">{{ m }}</th>
                        <th>Total</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="name in requesterPivot.names" :key="name">
                        <td class="fw-bold">{{ name }}</td>
                        <td v-for="m in requesterPivot.months" :key="m">
                            <span v-if="requesterPivot.data[name][m]"
                                  :class="['crh-cell-val', requesterPivot.data[name][m] >= 5 ? 'high' : requesterPivot.data[name][m] >= 3 ? 'mid' : 'low']">
                              {{ requesterPivot.data[name][m] }}
                            </span>
                          <span v-else class="text-muted">—</span>
                        </td>
                        <td class="fw-bold text-danger">{{ requesterTotals[name] }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- By department + sub county + payment type -->
                <div class="crh-three-col">

                  <div class="crh-card">
                    <div class="crh-card-hdr"><h6><i class="bx bx-buildings me-2"></i>By Department</h6></div>
                    <div v-if="analytics.byDepartment.length === 0" class="crh-no-data p-3">No data</div>
                    <div v-else>
                      <div v-for="d in analytics.byDepartment" :key="d.department" class="crh-hbar-row">
                        <div class="crh-hbar-label">{{ d.department }}</div>
                        <div class="crh-hbar-track"><div class="crh-hbar-fill blue" :style="{ width: (d.totalCorrections / analytics.byDepartment[0].totalCorrections * 100) + '%' }"></div></div>
                        <div class="crh-hbar-count">{{ d.totalCorrections }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="crh-card">
                    <div class="crh-card-hdr"><h6><i class="bx bx-map me-2"></i>By Sub County</h6></div>
                    <div v-if="analytics.bySubCounty.length === 0" class="crh-no-data p-3">No data</div>
                    <div v-else>
                      <div v-for="s in analytics.bySubCounty" :key="s.subCountyName" class="crh-hbar-row">
                        <div class="crh-hbar-label">{{ s.subCountyName }}</div>
                        <div class="crh-hbar-track"><div class="crh-hbar-fill purple" :style="{ width: (s.totalCorrections / analytics.bySubCounty[0].totalCorrections * 100) + '%' }"></div></div>
                        <div class="crh-hbar-count">{{ s.totalCorrections }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="crh-card">
                    <div class="crh-card-hdr"><h6><i class="bx bx-credit-card me-2"></i>By Payment Type</h6></div>
                    <div v-if="analytics.byPaymentType.length === 0" class="crh-no-data p-3">No data</div>
                    <div v-else>
                      <div v-for="p in analytics.byPaymentType" :key="p.paymentType" class="crh-hbar-row">
                        <div class="crh-hbar-label">{{ p.paymentType }}</div>
                        <div class="crh-hbar-track"><div class="crh-hbar-fill green" :style="{ width: (p.totalCorrections / analytics.byPaymentType[0].totalCorrections * 100) + '%' }"></div></div>
                        <div class="crh-hbar-count">{{ p.totalCorrections }}</div>
                      </div>
                    </div>
                  </div>

                </div>

              </template>
            </div>

          </div>
        </div>

        <!-- DETAIL MODAL -->
        <div class="modal fade" id="detailModal" tabindex="-1">
          <div class="modal-dialog modal-dialog-centered modal-lg">
            <div class="modal-content crh-modal">
              <div class="crh-modal-hdr">
                <h5><i class="bx bx-revision me-2"></i>Correction Details</h5>
                <button class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
              </div>
              <div class="modal-body p-4" v-if="selectedCorrection">
                <div class="crh-detail-grid">

                  <!-- Left: Original Transaction -->
                  <div class="crh-detail-section">
                    <div class="crh-section-title"><i class="bx bx-receipt"></i> Original Transaction</div>
                    <div class="crh-detail-row"><span>Transaction Code</span><strong>{{ selectedCorrection.transaction_code }}</strong></div>
                    <div class="crh-detail-row"><span>Bill No</span><strong>{{ selectedCorrection.billNo }}</strong></div>
                    <div class="crh-detail-row"><span>Amount</span><strong class="text-primary">{{ formatPrice(selectedCorrection.receiptAmount || selectedCorrection.amount) }}</strong></div>
                    <div class="crh-detail-row"><span>Payment Type</span><span :class="['crh-type-badge', (selectedCorrection.payment_type || '').toLowerCase()]">{{ selectedCorrection.payment_type }}</span></div>
                    <div class="crh-detail-row"><span>Payer Name</span><strong>{{ selectedCorrection.names }}</strong></div>
                    <div class="crh-detail-row">
                      <span>Phone / Account</span>
                      <span :title="selectedCorrection.account_from" style="cursor:help;">
                        {{ selectedCorrection.account_from ? selectedCorrection.account_from.substring(0, 20) + (selectedCorrection.account_from.length > 20 ? '...' : '') : '—' }}
                      </span>
                    </div>
                    <div class="crh-detail-row"><span>Transaction Date</span><span>{{ formatDateTime(selectedCorrection.transactionDate) }}</span></div>
                    <div class="crh-detail-row"><span>Sub County</span><span>{{ selectedCorrection.subCountyName }}</span></div>
                    <div class="crh-detail-row"><span>Ward</span><span>{{ selectedCorrection.wardName }}</span></div>
                    <div class="crh-detail-row"><span>Department</span><span>{{ selectedCorrection.department || '—' }}</span></div>
                    <div class="crh-detail-row"><span>Income Type</span><span>{{ selectedCorrection.incomeTypeDescription || '—' }}</span></div>
                    <div class="crh-detail-row"><span>Receipted By</span><span>{{ selectedCorrection.receiptedBy || '—' }}</span></div>
                  </div>

                  <!-- Right: Correction Info -->
                  <div class="crh-detail-section">
                    <div class="crh-section-title correction"><i class="bx bx-edit-alt"></i> Correction Info</div>

                    <div class="crh-correction-highlight">
                      <div class="crh-correction-label">What was corrected</div>
                      <div class="crh-correction-text">{{ selectedCorrection.correctionDetails || 'No details provided' }}</div>
                    </div>

                    <div class="crh-detail-row mt-3"><span>Correction Type</span><span class="crh-correction-type-badge">{{ selectedCorrection.correction }}</span></div>

                    <div class="crh-section-sub mt-3">REQUEST</div>
                    <div class="crh-detail-row"><span>Requested By</span><strong>{{ selectedCorrection.correctionRequestedBy || '—' }}</strong></div>
                    <div class="crh-detail-row"><span>Requester ID</span><span>{{ selectedCorrection.correctionRequestedByIDNo || '—' }}</span></div>
                    <div class="crh-detail-row"><span>Request Date</span><span>{{ formatDateTime(selectedCorrection.correctionDate) }}</span></div>

                    <div class="crh-section-sub mt-3">APPROVAL</div>
                    <div class="crh-detail-row"><span>Approved By</span><strong class="text-success">{{ selectedCorrection.correctionApprovalBy || '—' }}</strong></div>
                    <div class="crh-detail-row"><span>Approver ID</span><span>{{ selectedCorrection.correctionApprovalByIDNo || '—' }}</span></div>
                    <div class="crh-detail-row"><span>Approval Date</span><strong class="text-success">{{ formatDateTime(selectedCorrection.correctionDateApproved) }}</strong></div>

                    <!-- Timeline -->
                    <div class="crh-timeline mt-3">
                      <div class="crh-tl-item">
                        <div class="crh-tl-dot request"></div>
                        <div>
                          <div class="crh-tl-label">Correction Requested</div>
                          <div class="crh-tl-val">{{ selectedCorrection.correctionRequestedBy }} — {{ formatDateTime(selectedCorrection.correctionDate) }}</div>
                        </div>
                      </div>
                      <div class="crh-tl-line"></div>
                      <div class="crh-tl-item">
                        <div class="crh-tl-dot approved"></div>
                        <div>
                          <div class="crh-tl-label">Correction Approved</div>
                          <div class="crh-tl-val">{{ selectedCorrection.correctionApprovalBy }} — {{ formatDateTime(selectedCorrection.correctionDateApproved) }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="modal-footer border-0 px-4 pb-4">
                <button class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
              </div>
            </div>
          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6">© 2024 County Revenue System</div>
              <div class="col-sm-6 text-sm-end">Craft Silicon</div>
            </div>
          </div>
        </footer>
      </div>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import { biller, execute } from "@/api";
import NavigationBar from "@/components/Navigation.vue";
import moment from "moment";

export default {
  name: "CorrectionHistory",
  components: { NavigationBar },

  data() {
    return {
      activeTab:        'analytics',

      // List tab
      corrections:      [],
      loadingList:      false,
      pagination:       { total: 0, page: 1, perPage: 50, totalPages: 1 },
      filters:          { keyword: '', dateFrom: '', dateTo: '' },
      selectedCorrection: null,
      generatingListPDF:  false,

      // Analytics tab
      analyticsLoaded:  false,
      loadingAnalytics: false,
      generatingPDF:    false,
      aDateFrom:        moment().startOf('year').format('YYYY-MM-DD'),
      aDateTo:          moment().format('YYYY-MM-DD'),
      quickActive:      'thisYear',
      analytics: {
        summary:       {},
        topRequesters: [],
        topApprovers:  [],
        monthlyTrend:  [],
        byDepartment:  [],
        bySubCounty:   [],
        byPaymentType: [],
        requesterTrend: []
      }
    };
  },

  computed: {
    maxMonthly() {
      if (!this.analytics.monthlyTrend.length) return 1;
      return Math.max(...this.analytics.monthlyTrend.map(m => parseInt(m.totalCorrections)));
    },
    requesterPivot() {
      const rt    = this.analytics.requesterTrend;
      const names = [...new Set(rt.map(r => r.name))];
      const months = [...new Set(rt.map(r => r.monthLabel))];
      const data  = {};
      names.forEach(n => {
        data[n] = {};
        rt.filter(r => r.name === n).forEach(r => { data[n][r.monthLabel] = parseInt(r.totalRequests); });
      });
      return { names, months, data };
    },
    requesterTotals() {
      const t = {};
      this.requesterPivot.names.forEach(n => {
        t[n] = Object.values(this.requesterPivot.data[n]).reduce((s, v) => s + v, 0);
      });
      return t;
    }
  },

  mounted() {
    this.loadAnalytics();
  },

  methods: {

    // ── List ──────────────────────────────────────────────────────────────
    async loadList() {
      this.loadingList = true;
      try {
        const form = new FormData();
        form.append('function', 'getCorrectionHistory');
        form.append('keyword',  this.filters.keyword);
        form.append('dateFrom', this.filters.dateFrom);
        form.append('dateTo',   this.filters.dateTo);
        form.append('page',     this.pagination.page);
        form.append('perPage',  this.pagination.perPage);
        const res = await execute(form, biller);
        if (res.data.success) {
          this.corrections.splice(0, this.corrections.length, ...(res.data.data.corrections || []));
          this.pagination = res.data.data.pagination || this.pagination;
        } else {
          this.corrections.splice(0, this.corrections.length);
        }
      } catch (e) { console.error(e); }
      finally { this.loadingList = false; }
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages) return;
      this.pagination.page = page;
      this.loadList();
    },

    resetFilters() {
      this.filters = { keyword: '', dateFrom: '', dateTo: '' };
      this.pagination.page = 1;
      this.loadList();
    },

    openDetail(c) {
      this.selectedCorrection = c;
      new bootstrap.Modal(document.getElementById('detailModal')).show();
    },

    async downloadListPDF() {
      this.generatingListPDF = true;
      try {
        await this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
        await this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js');

        // Fetch ALL records (not just current page)
        const form = new FormData();
        form.append('function', 'getCorrectionHistory');
        form.append('keyword',  this.filters.keyword);
        form.append('dateFrom', this.filters.dateFrom);
        form.append('dateTo',   this.filters.dateTo);
        form.append('page',     1);
        form.append('perPage',  99999);
        const res = await execute(form, biller);
        const allRecords = res.data.success ? (res.data.data.corrections || []) : this.corrections;

        const { jsPDF } = window.jspdf;
        const doc    = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        const pageW  = doc.internal.pageSize.getWidth();
        const pageH  = doc.internal.pageSize.getHeight();
        const margin = 14;

        const primaryColor = [15, 52, 96];
        const accentColor  = [220, 38, 38];
        const lightGray    = [249, 250, 251];
        const textMuted    = [107, 114, 128];
        const borderGray   = [229, 231, 235];

        // Cover header
        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageW, 36, 'F');
        doc.setFillColor(...accentColor);
        doc.rect(0, 32, pageW, 4, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('CORRECTION HISTORY REPORT', pageW / 2, 14, { align: 'center' });
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text('Kajiado County Revenue Management System', pageW / 2, 21, { align: 'center' });
        doc.setFontSize(8);
        doc.setTextColor(200, 210, 230);
        const filterDesc = this.filters.keyword
            ? `Search: "${this.filters.keyword}"`
            : (this.filters.dateFrom && this.filters.dateTo ? `Period: ${this.filters.dateFrom} to ${this.filters.dateTo}` : 'All Records');
        doc.text(`Filter: ${filterDesc}   |   Total Records: ${allRecords.length}`, pageW / 2, 27, { align: 'center' });
        doc.text(`Generated: ${this.formatDateTime(new Date())}`, pageW / 2, 31, { align: 'center' });

        // Table
        doc.autoTable({
          startY: 44,
          margin: { left: margin, right: margin },
          head: [[
            '#', 'Transaction Code', 'Bill No', 'Amount', 'Payment Type',
            'Correction Details', 'Requested By', 'Request Date',
            'Approved By', 'Approval Date'
          ]],
          body: allRecords.map(c => [
            c.id,
            c.transaction_code,
            c.billNo,
            this.formatPrice(c.receiptAmount || c.amount),
            c.payment_type,
            c.correctionDetails || '—',
            c.correctionRequestedBy || '—',
            this.formatDateTime(c.correctionDate),
            c.correctionApprovalBy || '—',
            this.formatDateTime(c.correctionDateApproved)
          ]),
          styles:      { fontSize: 7, cellPadding: 2.5, overflow: 'linebreak' },
          headStyles:  { fillColor: primaryColor, textColor: 255, fontStyle: 'bold', fontSize: 7 },
          alternateRowStyles: { fillColor: lightGray },
          columnStyles: {
            0: { cellWidth: 12, halign: 'center' },
            1: { cellWidth: 28 },
            2: { cellWidth: 22 },
            3: { cellWidth: 24, halign: 'right' },
            4: { cellWidth: 18, halign: 'center' },
            5: { cellWidth: 44 },
            6: { cellWidth: 26 },
            7: { cellWidth: 24 },
            8: { cellWidth: 26 },
            9: { cellWidth: 24 }
          },
          didDrawPage: (data) => {
            doc.setFontSize(7);
            doc.setTextColor(...textMuted);
            doc.setFont('helvetica', 'normal');
            doc.text('Kajiado County Revenue System — Correction History Report', margin, pageH - 8);
            doc.text(`Page ${data.pageNumber}`, pageW - margin, pageH - 8, { align: 'right' });
            doc.setDrawColor(...borderGray);
            doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
          }
        });

        const datePart = this.filters.dateFrom && this.filters.dateTo
            ? `_${this.filters.dateFrom}_to_${this.filters.dateTo}`
            : `_${this.formatDate(new Date())}`;
        doc.save(`Correction_History${datePart}.pdf`);

      } catch (e) {
        console.error('PDF generation failed:', e);
        alert('PDF generation failed: ' + e.message);
      } finally {
        this.generatingListPDF = false;
      }
    },

    // ── PDF Export ────────────────────────────────────────────────────────
    loadScript(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
        const s = document.createElement('script');
        s.src = src;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
    },

    async downloadPDF() {
      this.generatingPDF = true;
      try {
        await this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
        await this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js');

        const { jsPDF } = window.jspdf;
        const doc  = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageW  = doc.internal.pageSize.getWidth();
        const pageH  = doc.internal.pageSize.getHeight();
        const margin = 14;
        let y = margin;

        const primaryColor = [15, 52, 96];
        const accentColor  = [220, 38, 38];
        const successColor = [5, 150, 105];
        const warningColor = [217, 119, 6];
        const lightGray    = [249, 250, 251];
        const textDark     = [31, 41, 55];
        const textMuted    = [107, 114, 128];
        const borderGray   = [229, 231, 235];

        const sectionHeader = (title) => {
          if (y + 12 > pageH - 20) { doc.addPage(); y = margin; }
          doc.setFillColor(...primaryColor);
          doc.rect(margin, y, pageW - margin * 2, 8, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.text(title, margin + 3, y + 5.5);
          y += 11;
        };

        const addPageFooter = () => {
          const pages = doc.internal.getNumberOfPages();
          for (let i = 1; i <= pages; i++) {
            doc.setPage(i);
            doc.setFontSize(7);
            doc.setTextColor(...textMuted);
            doc.setFont('helvetica', 'normal');
            doc.text('Kajiado County Revenue System — Correction Analytics Report', margin, pageH - 8);
            doc.text(`Page ${i} of ${pages}`, pageW - margin, pageH - 8, { align: 'right' });
            doc.setDrawColor(...borderGray);
            doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
          }
        };

        // Cover header
        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageW, 42, 'F');
        doc.setFillColor(...accentColor);
        doc.rect(0, 38, pageW, 4, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text('CORRECTION ANALYTICS REPORT', pageW / 2, 16, { align: 'center' });
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text('Kajiado County Revenue Management System', pageW / 2, 24, { align: 'center' });
        doc.setFontSize(8);
        doc.setTextColor(200, 210, 230);
        doc.text(`Report Period: ${this.aDateFrom} to ${this.aDateTo}`, pageW / 2, 31, { align: 'center' });
        doc.text(`Generated: ${this.formatDateTime(new Date())}`, pageW / 2, 36, { align: 'center' });
        y = 52;

        // Summary cards
        const s     = this.analytics.summary;
        const cardW = (pageW - margin * 2 - 9) / 4;
        [
          { label: 'Total Corrections',  value: s.totalCorrections,                       color: accentColor },
          { label: 'Amount Corrected',   value: this.formatPrice(s.totalAmountCorrected),  color: warningColor },
          { label: 'Unique Requesters',  value: s.uniqueRequesters,                        color: primaryColor },
          { label: 'Unique Approvers',   value: s.uniqueApprovers,                         color: successColor },
        ].forEach((card, i) => {
          const x = margin + i * (cardW + 3);
          doc.setFillColor(...lightGray);
          doc.roundedRect(x, y, cardW, 18, 2, 2, 'F');
          doc.setFillColor(...card.color);
          doc.roundedRect(x, y, 3, 18, 1, 1, 'F');
          doc.setTextColor(...textDark);
          doc.setFontSize(13);
          doc.setFont('helvetica', 'bold');
          doc.text(String(card.value), x + cardW / 2 + 2, y + 9, { align: 'center' });
          doc.setFontSize(6.5);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...textMuted);
          doc.text(card.label.toUpperCase(), x + cardW / 2 + 2, y + 14, { align: 'center' });
        });
        y += 24;

        // Monthly trend
        if (this.analytics.monthlyTrend.length > 0) {
          sectionHeader('MONTHLY CORRECTION TREND');
          doc.autoTable({
            startY: y, margin: { left: margin, right: margin },
            head: [['Month', 'Corrections', 'Amount (KES)']],
            body: this.analytics.monthlyTrend.map(m => [m.monthLabel, m.totalCorrections, this.formatPrice(m.totalAmount)]),
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: primaryColor, textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 40, halign: 'center' }, 2: { halign: 'right' } }
          });
          y = doc.lastAutoTable.finalY + 8;
        }

        // Top requesters
        if (this.analytics.topRequesters.length > 0) {
          if (y + 40 > pageH - 20) { doc.addPage(); y = margin; }
          sectionHeader('TOP CORRECTION REQUESTERS');
          doc.autoTable({
            startY: y, margin: { left: margin, right: margin },
            head: [['#', 'Officer', 'ID No', 'Requests', 'Amount (KES)']],
            body: this.analytics.topRequesters.map((r, i) => [i + 1, r.name, r.idNo, r.totalRequests, this.formatPrice(r.totalAmountRequested)]),
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: accentColor, textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: { 0: { cellWidth: 12, halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'right' } }
          });
          y = doc.lastAutoTable.finalY + 8;
        }

        // Top approvers
        if (this.analytics.topApprovers.length > 0) {
          if (y + 40 > pageH - 20) { doc.addPage(); y = margin; }
          sectionHeader('TOP APPROVERS');
          doc.autoTable({
            startY: y, margin: { left: margin, right: margin },
            head: [['#', 'Approver', 'ID No', 'Approvals', 'Amount (KES)']],
            body: this.analytics.topApprovers.map((a, i) => [i + 1, a.name, a.idNo, a.totalApprovals, this.formatPrice(a.totalAmountApproved)]),
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: warningColor, textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: { 0: { cellWidth: 12, halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'right' } }
          });
          y = doc.lastAutoTable.finalY + 8;
        }

        // Requester trend pivot
        if (this.requesterPivot.names.length > 0 && this.requesterPivot.months.length > 0) {
          if (y + 40 > pageH - 20) { doc.addPage(); y = margin; }
          sectionHeader('REQUESTER TREND — TOP 5 OVER TIME');
          doc.autoTable({
            startY: y, margin: { left: margin, right: margin },
            head: [['Officer', ...this.requesterPivot.months, 'Total']],
            body: this.requesterPivot.names.map(name => [
              name,
              ...this.requesterPivot.months.map(m => this.requesterPivot.data[name][m] || '—'),
              this.requesterTotals[name]
            ]),
            styles: { fontSize: 7.5, cellPadding: 2.5 },
            headStyles: { fillColor: primaryColor, textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: lightGray }
          });
          y = doc.lastAutoTable.finalY + 8;
        }

        // By department
        if (this.analytics.byDepartment.length > 0) {
          if (y + 40 > pageH - 20) { doc.addPage(); y = margin; }
          sectionHeader('CORRECTIONS BY DEPARTMENT');
          doc.autoTable({
            startY: y, margin: { left: margin, right: margin },
            head: [['Department', 'Corrections', 'Amount (KES)']],
            body: this.analytics.byDepartment.map(d => [d.department, d.totalCorrections, this.formatPrice(d.totalAmount)]),
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' } }
          });
          y = doc.lastAutoTable.finalY + 8;
        }

        // By sub county
        if (this.analytics.bySubCounty.length > 0) {
          if (y + 40 > pageH - 20) { doc.addPage(); y = margin; }
          sectionHeader('CORRECTIONS BY SUB COUNTY');
          doc.autoTable({
            startY: y, margin: { left: margin, right: margin },
            head: [['Sub County', 'Corrections', 'Amount (KES)']],
            body: this.analytics.bySubCounty.map(s => [s.subCountyName, s.totalCorrections, this.formatPrice(s.totalAmount)]),
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' } }
          });
          y = doc.lastAutoTable.finalY + 8;
        }

        // By payment type
        if (this.analytics.byPaymentType.length > 0) {
          if (y + 40 > pageH - 20) { doc.addPage(); y = margin; }
          sectionHeader('CORRECTIONS BY PAYMENT TYPE');
          doc.autoTable({
            startY: y, margin: { left: margin, right: margin },
            head: [['Payment Type', 'Corrections', 'Amount (KES)']],
            body: this.analytics.byPaymentType.map(p => [p.paymentType, p.totalCorrections, this.formatPrice(p.totalAmount)]),
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: { fillColor: successColor, textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: lightGray },
            columnStyles: { 1: { halign: 'center' }, 2: { halign: 'right' } }
          });
        }

        addPageFooter();
        doc.save(`Correction_Analytics_${this.aDateFrom}_to_${this.aDateTo}.pdf`);

      } catch (e) {
        console.error('PDF generation failed:', e);
        alert('PDF generation failed: ' + e.message);
      } finally {
        this.generatingPDF = false;
      }
    },

    // ── Analytics ─────────────────────────────────────────────────────────
    async loadAnalytics() {
      this.loadingAnalytics = true;
      this.analyticsLoaded  = false;
      try {
        const form = new FormData();
        form.append('function', 'getCorrectionAnalytics');
        form.append('dateFrom', this.aDateFrom);
        form.append('dateTo',   this.aDateTo);
        const res = await execute(form, biller);
        if (res.data.success) {
          const d = res.data.data;
          this.analytics.summary = d.summary || {};
          this.analytics.topRequesters.splice(0,  this.analytics.topRequesters.length,  ...(d.topRequesters  || []));
          this.analytics.topApprovers.splice(0,   this.analytics.topApprovers.length,   ...(d.topApprovers   || []));
          this.analytics.monthlyTrend.splice(0,   this.analytics.monthlyTrend.length,   ...(d.monthlyTrend   || []));
          this.analytics.byDepartment.splice(0,   this.analytics.byDepartment.length,   ...(d.byDepartment   || []));
          this.analytics.bySubCounty.splice(0,    this.analytics.bySubCounty.length,    ...(d.bySubCounty    || []));
          this.analytics.byPaymentType.splice(0,  this.analytics.byPaymentType.length,  ...(d.byPaymentType  || []));
          this.analytics.requesterTrend.splice(0, this.analytics.requesterTrend.length, ...(d.requesterTrend || []));
          this.analyticsLoaded = true;
        }
      } catch (e) { console.error(e); }
      finally { this.loadingAnalytics = false; }
    },

    setQuick(period) {
      this.quickActive = period;
      if (period === 'thisYear')  { this.aDateFrom = moment().startOf('year').format('YYYY-MM-DD');      this.aDateTo = moment().format('YYYY-MM-DD'); }
      if (period === 'thisMonth') { this.aDateFrom = moment().startOf('month').format('YYYY-MM-DD');     this.aDateTo = moment().format('YYYY-MM-DD'); }
      if (period === 'last3')     { this.aDateFrom = moment().subtract(3,'months').format('YYYY-MM-DD'); this.aDateTo = moment().format('YYYY-MM-DD'); }
      this.loadAnalytics();
    },

    barHeight(val, max) { return Math.max(Math.round((parseInt(val) / max) * 120), 6); },
    barColor(val, max)  {
      const p = (val / max) * 100;
      return p >= 75 ? 'danger' : p >= 40 ? 'warn' : 'ok';
    },

    // ── Helpers ───────────────────────────────────────────────────────────
    formatPrice(v)    { return new Intl.NumberFormat('en-KE', { style:'currency', currency:'KES', minimumFractionDigits:2 }).format(parseFloat(v)||0); },
    formatDate(d)     { return d ? moment(d).format('DD MMM YYYY') : '—'; },
    formatDateTime(d) { return d ? moment(d).format('DD MMM YYYY HH:mm') : '—'; },
    gotTo(r)          { this.$router.push(r); }
  }
};
</script>

<style scoped>
/* Header */
.crh-header { display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:1rem;
  border-radius:14px;padding:1.5rem 2rem;margin-bottom:1.25rem;
  background:linear-gradient(135deg,#1a1a2e 0%,#16213e 50%,#0f3460 100%);
  box-shadow:0 4px 24px rgba(0,0,0,.3); }
.crh-header-left { display:flex;align-items:center;gap:1rem; }
.crh-header-icon { width:56px;height:56px;border-radius:14px;display:flex;align-items:center;justify-content:center;
  font-size:1.75rem;background:rgba(245,158,11,.2);color:#fbbf24; }
.crh-title { color:white;font-size:1.4rem;font-weight:700;margin:0 0 .25rem; }
.crh-breadcrumb { display:flex;align-items:center;gap:.4rem;color:rgba(255,255,255,.6);font-size:.8rem; }
.crh-bc-link { cursor:pointer; } .crh-bc-link:hover { color:white; } .crh-bc-sep { opacity:.4; }
.crh-header-stat { text-align:center; }
.crh-stat-val { display:block;font-size:2rem;font-weight:800;color:white;line-height:1; }
.crh-stat-lbl { font-size:.7rem;font-weight:600;color:rgba(255,255,255,.6);text-transform:uppercase;letter-spacing:.5px; }

/* Tabs */
.crh-tabs { display:flex;gap:.5rem;margin-bottom:1.25rem;border-bottom:2px solid #e5e7eb;padding-bottom:.5rem; }
.crh-tab { background:none;border:none;padding:.5rem 1.25rem;font-size:.875rem;font-weight:600;
  color:#6b7280;cursor:pointer;border-radius:8px 8px 0 0;transition:all .2s;display:flex;align-items:center; }
.crh-tab:hover { color:#374151;background:#f9fafb; }
.crh-tab.active { color:#4f46e5;background:#ede9fe;border-bottom:2px solid #4f46e5; }

/* Filters */
.crh-filters { display:flex;align-items:flex-end;gap:.75rem;margin-bottom:1.25rem;flex-wrap:wrap;
  background:white;border-radius:12px;padding:1rem 1.25rem;box-shadow:0 2px 8px rgba(0,0,0,.06); }
.crh-search { flex:1;min-width:260px;height:38px;padding:0 .875rem;border:1px solid #d1d5db;border-radius:8px;font-size:.875rem; }
.crh-date-group { display:flex;flex-direction:column;gap:.25rem; }
.crh-date-lbl { font-size:.68rem;font-weight:700;text-transform:uppercase;color:#6b7280;letter-spacing:.4px; }
.crh-datepicker { height:38px;padding:0 .75rem;border:1px solid #d1d5db;border-radius:8px;font-size:.875rem; }
.crh-search-btn { height:38px;padding:0 1.25rem;border:none;border-radius:8px;
  background:linear-gradient(135deg,#4f46e5,#7c3aed);color:white;font-size:.875rem;
  font-weight:600;cursor:pointer;display:flex;align-items:center;transition:all .2s; }
.crh-search-btn:hover:not(:disabled) { filter:brightness(1.1); }
.crh-search-btn:disabled { opacity:.6; }
.crh-reset-btn { height:38px;width:38px;border:1px solid #d1d5db;border-radius:8px;
  background:white;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:1.1rem;color:#6b7280; }
.crh-pdf-btn { height:38px;padding:0 1.25rem;border:none;border-radius:8px;
  background:linear-gradient(135deg,#dc2626,#ef4444);color:white;font-size:.875rem;
  font-weight:600;cursor:pointer;display:flex;align-items:center;gap:.4rem;
  margin-left:auto;transition:all .2s;white-space:nowrap; }
.crh-pdf-btn:hover:not(:disabled) { filter:brightness(1.1); }
.crh-pdf-btn:disabled { opacity:.6;cursor:not-allowed; }
.crh-quick-btns { display:flex;gap:.4rem; }
.crh-quick { height:34px;padding:0 .875rem;border:1px solid #d1d5db;border-radius:99px;
  background:white;color:#6b7280;font-size:.75rem;cursor:pointer;transition:all .2s; }
.crh-quick.active,.crh-quick:hover { background:#ede9fe;color:#4f46e5;border-color:#a5b4fc; }

/* Loading / empty */
.crh-loading { text-align:center;padding:3rem;color:#6b7280; }
.crh-loading p { margin-top:.75rem; }
.crh-empty { text-align:center;padding:4rem; }
.crh-empty-icon { font-size:3.5rem;color:#10b981;display:block;margin-bottom:.75rem; }

/* List table */
.crh-table-wrap { background:white;border-radius:12px;box-shadow:0 2px 8px rgba(0,0,0,.06);overflow:hidden; }
.crh-table { font-size:.8375rem;margin:0; }
.crh-table thead tr { background:#f9fafb;font-size:.7rem;text-transform:uppercase;letter-spacing:.5px;color:#6b7280; }
.crh-table thead th { padding:.875rem 1rem;border-bottom:2px solid #e5e7eb; }
.crh-table tbody td { padding:.75rem 1rem;border-bottom:1px solid #f3f4f6;vertical-align:middle; }
.crh-table tbody tr:hover { background:#fafafa; }
.crh-table tbody tr:last-child td { border-bottom:none; }
.crh-tx-code { font-weight:700;font-size:.8125rem;color:#1f2937; }
.crh-tx-sub  { font-size:.75rem;color:#6b7280;margin:.15rem 0; }
.crh-type-badge { display:inline-block;padding:.15rem .5rem;border-radius:99px;
  font-size:.65rem;font-weight:700;text-transform:uppercase; }
.crh-type-badge.mpesa   { background:rgba(16,185,129,.1);color:#059669; }
.crh-type-badge.bank    { background:rgba(37,99,235,.1);color:#1d4ed8; }
.crh-type-badge.offline,.crh-type-badge.cash { background:rgba(99,102,241,.1);color:#4f46e5; }
.crh-correction-box { background:#fef3c7;border-left:3px solid #f59e0b;
  padding:.5rem .75rem;border-radius:6px;font-size:.8rem;color:#92400e;max-width:280px;line-height:1.5; }
.crh-approved-badge { display:inline-flex;align-items:center;background:#d1fae5;
  color:#065f46;padding:.2rem .625rem;border-radius:99px;font-size:.72rem;font-weight:600; }
.crh-detail-btn { height:28px;padding:0 .75rem;border:1px solid #d1d5db;border-radius:6px;
  background:white;font-size:.78rem;cursor:pointer;display:inline-flex;align-items:center;gap:.25rem; }
.crh-detail-btn:hover { background:#f3f4f6; }
.crh-pagination { display:flex;align-items:center;justify-content:center;gap:1rem;padding:1rem;border-top:1px solid #f3f4f6; }
.crh-page-btn { height:32px;width:32px;border:1px solid #d1d5db;border-radius:7px;background:white;
  cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:1rem;color:#374151; }
.crh-page-btn:disabled { opacity:.4;cursor:not-allowed; }
.crh-page-info { font-size:.8125rem;color:#6b7280; }

/* Detail modal */
.crh-modal { border:none;border-radius:14px;box-shadow:0 20px 60px rgba(0,0,0,.2);overflow:hidden; }
.crh-modal-hdr { padding:1.25rem 1.5rem;display:flex;justify-content:space-between;align-items:center;
  background:linear-gradient(135deg,#1a1a2e,#0f3460);color:white; }
.crh-modal-hdr h5 { margin:0;font-weight:600;display:flex;align-items:center; }
.crh-detail-grid { display:grid;grid-template-columns:1fr 1fr;gap:1.5rem; }
.crh-detail-section { background:#f9fafb;border-radius:10px;padding:1.25rem;min-width:0; }
.crh-section-title { display:flex;align-items:center;gap:.5rem;font-size:.8rem;font-weight:700;
  text-transform:uppercase;letter-spacing:.5px;color:#374151;margin-bottom:.875rem;
  padding-bottom:.5rem;border-bottom:2px solid #e5e7eb; }
.crh-section-title.correction { color:#4f46e5;border-bottom-color:rgba(79,70,229,.3); }
.crh-section-sub { font-size:.68rem;font-weight:800;text-transform:uppercase;letter-spacing:.6px;color:#9ca3af;margin-bottom:.35rem; }
.crh-detail-row { display:flex;justify-content:space-between;align-items:flex-start;
  padding:.35rem 0;border-bottom:1px solid #f3f4f6;font-size:.8125rem;gap:.5rem; }
.crh-detail-row:last-child { border-bottom:none; }
.crh-detail-row span:first-child { color:#6b7280;flex-shrink:0;white-space:nowrap; }
.crh-detail-row strong, .crh-detail-row span:last-child { text-align:right;word-break:break-all;overflow-wrap:anywhere;max-width:200px; }
.crh-correction-highlight { background:linear-gradient(135deg,rgba(245,158,11,.08),rgba(245,158,11,.15));
  border:1px solid rgba(245,158,11,.3);border-radius:8px;padding:1rem; }
.crh-correction-label { font-size:.68rem;font-weight:800;text-transform:uppercase;letter-spacing:.5px;color:#d97706;margin-bottom:.4rem; }
.crh-correction-text { font-size:.875rem;color:#1f2937;font-weight:500;line-height:1.6; }
.crh-correction-type-badge { display:inline-block;background:rgba(239,68,68,.1);color:#dc2626;padding:.2rem .625rem;border-radius:99px;font-size:.72rem;font-weight:700; }
.crh-timeline { padding:.75rem 0; }
.crh-tl-item { display:flex;align-items:flex-start;gap:.75rem; }
.crh-tl-dot { width:12px;height:12px;border-radius:50%;flex-shrink:0;margin-top:3px; }
.crh-tl-dot.request { background:#f59e0b; } .crh-tl-dot.approved { background:#10b981; }
.crh-tl-line { width:2px;height:20px;background:#e5e7eb;margin-left:5px; }
.crh-tl-label { font-size:.75rem;font-weight:700;color:#374151; }
.crh-tl-val   { font-size:.75rem;color:#6b7280; }

/* Analytics */
.crh-stat-row { display:grid;grid-template-columns:repeat(4,1fr);gap:1rem;margin-bottom:1.25rem; }
.crh-stat-card { display:flex;align-items:center;gap:1rem;background:white;
  border-radius:12px;padding:1.25rem;box-shadow:0 2px 8px rgba(0,0,0,.06);border-left:4px solid transparent; }
.crh-stat-card.red    { border-left-color:#dc2626; }
.crh-stat-card.orange { border-left-color:#ea580c; }
.crh-stat-card.blue   { border-left-color:#2563eb; }
.crh-stat-card.green  { border-left-color:#059669; }
.crh-stat-icon { width:44px;height:44px;border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:1.4rem;flex-shrink:0; }
.crh-stat-card.red    .crh-stat-icon { background:rgba(220,38,38,.1);color:#dc2626; }
.crh-stat-card.orange .crh-stat-icon { background:rgba(234,88,12,.1);color:#ea580c; }
.crh-stat-card.blue   .crh-stat-icon { background:rgba(37,99,235,.1);color:#2563eb; }
.crh-stat-card.green  .crh-stat-icon { background:rgba(5,150,105,.1);color:#059669; }
.crh-stat-val2 { font-size:1.3rem;font-weight:800;color:#1f2937;line-height:1; }
.crh-stat-lbl2 { font-size:.72rem;font-weight:600;color:#6b7280;text-transform:uppercase;letter-spacing:.4px;margin-top:.2rem; }
.crh-card { background:white;border-radius:12px;box-shadow:0 2px 8px rgba(0,0,0,.06);margin-bottom:1.25rem;overflow:hidden; }
.crh-card-hdr { padding:1rem 1.25rem;border-bottom:1px solid #f3f4f6; }
.crh-card-hdr h6 { margin:0 0 .2rem;font-weight:700;font-size:.9rem;display:flex;align-items:center;color:#1f2937; }
.crh-card-hdr small { color:#6b7280; }
.crh-card-hdr.danger h6 { color:#dc2626; }
.crh-card-hdr.warn   h6 { color:#d97706; }
.crh-no-data { text-align:center;color:#9ca3af;font-size:.875rem;padding:1.5rem; }
.crh-chart-wrap { padding:1.25rem;overflow-x:auto; }
.crh-bar-chart { display:flex;align-items:flex-end;gap:.75rem;min-height:160px;padding-bottom:.5rem; }
.crh-bar-group { display:flex;flex-direction:column;align-items:center;gap:.35rem;flex:1;min-width:60px;position:relative; }
.crh-bar-col { display:flex;flex-direction:column;align-items:center;gap:.25rem;width:100%;position:relative; }
.crh-bar-col:hover .crh-bar-tooltip { opacity:1; }
.crh-bar-tooltip { position:absolute;bottom:100%;left:50%;transform:translateX(-50%);
  background:#1f2937;color:white;font-size:.7rem;padding:.3rem .6rem;border-radius:6px;
  white-space:nowrap;opacity:0;transition:opacity .2s;z-index:10;pointer-events:none;margin-bottom:4px; }
.crh-bar { width:100%;border-radius:6px 6px 0 0;transition:height .4s ease;min-height:6px; }
.crh-bar.ok     { background:linear-gradient(180deg,#34d399,#10b981); }
.crh-bar.warn   { background:linear-gradient(180deg,#fbbf24,#f59e0b); }
.crh-bar.danger { background:linear-gradient(180deg,#f87171,#dc2626); }
.crh-bar-val { font-size:.7rem;font-weight:700;color:#374151; }
.crh-bar-lbl { font-size:.65rem;color:#9ca3af;text-align:center;white-space:nowrap;overflow:hidden;
  text-overflow:ellipsis;max-width:100%;transform:rotate(-30deg);transform-origin:top center;margin-top:.25rem; }
.crh-two-col   { display:grid;grid-template-columns:1fr 1fr;gap:1.25rem;margin-bottom:1.25rem; }
.crh-three-col { display:grid;grid-template-columns:1fr 1fr 1fr;gap:1.25rem;margin-bottom:1.25rem; }
.crh-leaderboard { padding:.5rem 0; }
.crh-lb-row { display:flex;align-items:center;gap:.75rem;padding:.625rem 1.25rem;border-bottom:1px solid #f9fafb; }
.crh-lb-row:last-child { border-bottom:none; }
.crh-lb-rank { width:20px;font-size:.8rem;font-weight:800;color:#9ca3af;text-align:center;flex-shrink:0; }
.crh-lb-rank.top { color:#f59e0b; }
.crh-lb-avatar { width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;
  font-size:.875rem;font-weight:700;color:white;flex-shrink:0;background:#9ca3af; }
.crh-lb-avatar.gold   { background:linear-gradient(135deg,#f59e0b,#fbbf24); }
.crh-lb-avatar.silver { background:linear-gradient(135deg,#9ca3af,#d1d5db); }
.crh-lb-avatar.bronze { background:linear-gradient(135deg,#b45309,#d97706); }
.crh-lb-info { flex:1;min-width:0; }
.crh-lb-name { font-weight:600;font-size:.8375rem;color:#1f2937;white-space:nowrap;overflow:hidden;text-overflow:ellipsis; }
.crh-lb-sub  { font-size:.72rem;color:#9ca3af; }
.crh-lb-right { text-align:right;min-width:100px; }
.crh-lb-count { font-size:1.1rem;font-weight:800;color:#374151;line-height:1; }
.crh-lb-count.danger { color:#dc2626; } .crh-lb-count.warn { color:#d97706; }
.crh-lb-amount { font-size:.7rem;color:#6b7280;margin:.15rem 0; }
.crh-lb-bar { height:4px;background:#f3f4f6;border-radius:99px;overflow:hidden;width:80px;margin-left:auto; }
.crh-lb-fill { height:100%;border-radius:99px; }
.crh-lb-fill.danger { background:#dc2626; } .crh-lb-fill.warn { background:#f59e0b; }
.crh-cell-val { display:inline-block;padding:.15rem .5rem;border-radius:99px;font-weight:700;font-size:.75rem; }
.crh-cell-val.high { background:#fee2e2;color:#dc2626; }
.crh-cell-val.mid  { background:#fef3c7;color:#d97706; }
.crh-cell-val.low  { background:#d1fae5;color:#059669; }
.crh-hbar-row { display:flex;align-items:center;gap:.75rem;padding:.5rem 1.25rem;border-bottom:1px solid #f9fafb;font-size:.8125rem; }
.crh-hbar-row:last-child { border-bottom:none; }
.crh-hbar-label { min-width:120px;max-width:160px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:#374151;font-weight:500; }
.crh-hbar-track { flex:1;height:8px;background:#f3f4f6;border-radius:99px;overflow:hidden; }
.crh-hbar-fill { height:100%;border-radius:99px;transition:width .4s ease; }
.crh-hbar-fill.blue   { background:linear-gradient(90deg,#2563eb,#60a5fa); }
.crh-hbar-fill.purple { background:linear-gradient(90deg,#7c3aed,#a78bfa); }
.crh-hbar-fill.green  { background:linear-gradient(90deg,#059669,#34d399); }
.crh-hbar-count { min-width:30px;text-align:right;font-weight:700;color:#374151;font-size:.8125rem; }

@media(max-width:1024px) {
  .crh-stat-row  { grid-template-columns:repeat(2,1fr); }
  .crh-two-col   { grid-template-columns:1fr; }
  .crh-three-col { grid-template-columns:1fr; }
  .crh-detail-grid { grid-template-columns:1fr; }
}
@media(max-width:768px) {
  .crh-stat-row { grid-template-columns:1fr 1fr; }
  .crh-filters  { flex-direction:column;align-items:stretch; }
  .crh-search   { min-width:unset; }
}
</style>